import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, Package, QrCode, Check } from "lucide-react";

export default function RescuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [kits, setKits] = useState([]);
  const [usedSlots, setUsedSlots] = useState(0);
  const [maxSlots, setMaxSlots] = useState(3);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [activating, setActivating] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    loadKits();
  }, [user, navigate]);

  const loadKits = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rescue/my-kits");
      setKits(data.kits || []);
      setUsedSlots(data.used_slots || 0);
      setMaxSlots(data.max_slots || 3);
    } catch { setKits([]); }
    finally { setLoading(false); }
  };

  const purchaseKit = async () => {
    setError(""); setSuccess("");
    setPurchasing(true);
    try {
      await api.post("/rescue/purchase-kit");
      setSuccess(t("rescue.purchased_ok"));
      loadKits();
    } catch (err) { setError(formatError(err)); }
    finally { setPurchasing(false); }
  };

  const activateBag = async (bagId, category, wearDuration) => {
    setError(""); setSuccess("");
    setActivating(bagId);
    try {
      await api.post("/rescue/activate-bag", { bag_id: bagId, category, wear_duration: wearDuration });
      setSuccess(t("rescue.activated_ok"));
      loadKits();
    } catch (err) { setError(formatError(err)); }
    finally { setActivating(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="rescue-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="rescue-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">{t("rescue.title_buyer")}</h1>
      </div>

      {error && <InfoCard variant="danger" size="sm">{error}</InfoCard>}
      {success && <InfoCard variant="success" size="sm">{success}</InfoCard>}

      <div className="px-4 py-4 border-b border-[#E5E5E5]">
        <p className="text-xs text-[#999] uppercase tracking-wider font-medium">{t("rescue.slots_this_month")}</p>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 h-2 bg-[#E5E5E5]">
            <div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${(usedSlots / maxSlots) * 100}%` }} />
          </div>
          <span className="text-sm font-bold text-[#0A0A0A]">{t("rescue.slots_free", { free: maxSlots - usedSlots, max: maxSlots })}</span>
        </div>
      </div>

      <div className="px-4 py-4 border-b border-[#E5E5E5]">
        <div className="p-4 border border-[#E5E5E5]">
          <div className="flex items-start gap-3">
            <Package size={24} className="text-[#999] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0A0A0A]">{t("rescue.purchase_title")}</p>
              <p className="text-xs text-[#999] mt-1 leading-relaxed">{t("rescue.purchase_desc")}</p>
              <p className="text-lg font-bold text-[#0A0A0A] mt-2">{t("rescue.purchase_price")}</p>
            </div>
          </div>
          <button data-testid="rescue-purchase" onClick={purchaseKit} disabled={purchasing}
            className="mt-3 w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
            {purchasing ? t("rescue.purchasing") : t("rescue.purchase_btn")}
          </button>
        </div>
      </div>

      <div className="px-4 py-4">
        <h2 className="text-xs font-bold text-[#999] uppercase tracking-wider mb-3">{t("rescue.my_kits")}</h2>
        {kits.length === 0 ? (
          <p className="text-sm text-[#999] text-center py-8">{t("rescue.no_kits")}</p>
        ) : (
          <div className="space-y-3">
            {kits.map((kit) => (
              <div key={kit.kit_id} data-testid={`rescue-kit-${kit.kit_id}`} className="border border-[#E5E5E5] p-3">
                <p className="text-xs text-[#999]">{new Date(kit.created_at).toLocaleDateString(i18n.language || "de")}</p>
                <div className="mt-2 space-y-2">
                  {kit.bags?.map((bag) => (
                    <BagItem key={bag.bag_id} bag={bag} activating={activating} onActivate={activateBag} t={t} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BagItem({ bag, activating, onActivate, t }) {
  const [category, setCategory] = useState("SOCKS");
  const [duration, setDuration] = useState("24h");
  const isActivated = bag.status === "ACTIVATED";

  if (isActivated) {
    return (
      <div className="flex items-center gap-2 p-2 bg-[#F5F5F5] border border-[#E5E5E5]">
        <Check size={14} className="text-green-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#0A0A0A]">{bag.category} – {bag.wear_duration}</p>
          <p className="text-[10px] text-[#999]">{t("rescue.bag_activated")}</p>
        </div>
        <QrCode size={14} className="text-[#999]" />
      </div>
    );
  }

  return (
    <div data-testid={`rescue-bag-${bag.bag_id}`} className="p-2 border border-[#E5E5E5]">
      <div className="flex gap-2 mb-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="flex-1 rounded-full h-8 px-2 border border-[#E5E5E5] text-xs bg-white">
          <option value="SOCKS">{t("rescue.cat_socks")}</option>
          <option value="SLIP">{t("rescue.cat_slip")}</option>
        </select>
        <select value={duration} onChange={(e) => setDuration(e.target.value)}
          className="flex-1 rounded-full h-8 px-2 border border-[#E5E5E5] text-xs bg-white">
          <option value="12h">{t("rescue.dur_12h")}</option>
          <option value="24h">{t("rescue.dur_24h")}</option>
          <option value="48h">{t("rescue.dur_48h")}</option>
          <option value="72h">{t("rescue.dur_72h")}</option>
        </select>
      </div>
      <button data-testid={`activate-bag-${bag.bag_id}`} onClick={() => onActivate(bag.bag_id, category, duration)}
        disabled={activating === bag.bag_id}
        className="w-full h-8 bg-[#0A0A0A] text-white text-xs font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
        {activating === bag.bag_id ? t("rescue.activating") : t("rescue.activate")}
      </button>
    </div>
  );
}
